import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  CircularProgress,
  Typography,
  Alert,
  Stack,
} from '@mui/material'
import PaginationControl from '@/apps/legacy/components/portal/PaginationControl'
import AdvancedSearchBox from '@/apps/legacy/components/portal/AdvancedSearchBox'
import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import { CustomButton } from '@/apps/legacy/components/portal/CustomButton'
import { Drug } from '@/features/internal/drugs/types/soapTemplate'
import { ADVANCED_SEARCH } from '@/shared/constants/const'

interface EnhancedDrugTableProps {
  /**
   * テーブルのモード（薬剤一覧 or 薬剤選択）
   */
  mode: 'drugs' | 'selection'

  /**
   * 表示する薬剤データ
   */
  data: Drug[]

  /**
   * 読み込み中フラグ
   */
  loading?: boolean

  /**
   * エラーメッセージ
   */
  error?: string | null

  /**
   * 通常検索クエリ文字列
   */
  normalSearchTerm: string

  /**
   * 通常検索クエリ変更時のコールバック
   */
  onNormalSearchChange: (query: string) => void

  /**
   * 通常検索実行時のコールバック
   */
  onNormalSearch: () => void

  /**
   * 高度検索実行時のコールバック
   */
  onAdvancedSearch: (conditions: AdvancedSearchCondition[]) => void

  /**
   * 現在のページ番号
   */
  currentPage?: number

  /**
   * 総ページ数
   */
  totalPages?: number

  /**
   * ページ変更時のコールバック
   */
  onPageChange?: (page: number) => void

  /**
   * 詳細表示ボタンクリック時のコールバック
   */
  onViewDetails?: (id: string) => void

  /**
   * 選択が変更されたときのコールバック（選択モードのみ）
   */
  onSelectionChange?: (selectedIds: string[]) => void

  /**
   * 現在選択されている薬剤IDのリスト（選択モードのみ）
   */
  selectedDrugIds?: string[]

  /**
   * 全件選択ボタンのコールバック（選択モードのみ）
   */
  onSelectAllResults?: (searchKeyword: string) => void

  /**
   * 現在の検索キーワード（全件選択用）
   */
  currentSearchKeyword?: string

  /**
   * 高度検索を有効にするかどうか
   */
  enableAdvancedSearch?: boolean

  /**
   * 検索プレースホルダー
   */
  searchPlaceholder?: string

  /**
   * 高度検索モード状態
   */
  isAdvancedMode?: boolean

  /**
   * 高度検索モード変更コールバック
   */
  onAdvancedModeChange?: (isAdvanced: boolean) => void

  /**
   * 初期の高度検索条件
   */
  initialAdvancedConditions?: AdvancedSearchCondition[]
}

/**
 * 高度検索機能付きの医薬品テーブルコンポーネント
 */
const EnhancedDrugTable: React.FC<EnhancedDrugTableProps> = ({
  mode,
  data,
  loading = false,
  error = null,
  normalSearchTerm,
  onNormalSearchChange,
  onNormalSearch,
  onAdvancedSearch,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onViewDetails,
  onSelectionChange,
  selectedDrugIds = [],
  onSelectAllResults,
  currentSearchKeyword = '',
  enableAdvancedSearch = true,
  searchPlaceholder = '医薬品名またはYJコードで検索',
  isAdvancedMode,
  onAdvancedModeChange,
  initialAdvancedConditions,
}) => {
  // 選択変更ハンドラー（選択モードのみ）
  const handleSelectionChange = (id: string, checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedDrugIds, id])
      } else {
        onSelectionChange(selectedDrugIds.filter((drugId) => drugId !== id))
      }
    }
  }

  // すべて選択/解除ハンドラー（現在のページのみ）
  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([
          ...selectedDrugIds,
          ...data
            .filter((drug) => !selectedDrugIds.includes(drug.id))
            .map((drug) => drug.id),
        ])
      } else {
        const currentPageIds = data.map((drug) => drug.id)
        onSelectionChange(
          selectedDrugIds.filter((id) => !currentPageIds.includes(id))
        )
      }
    }
  }

  // 全件選択ハンドラー
  const handleSelectAllResults = () => {
    if (onSelectAllResults && currentSearchKeyword) {
      onSelectAllResults(currentSearchKeyword)
    }
  }

  // 現在のページですべて選択されているかチェック
  const isAllSelected =
    data.length > 0 && data.every((drug) => selectedDrugIds.includes(drug.id))

  // 現在のページで一部選択されているかチェック
  const isIndeterminate =
    data.some((drug) => selectedDrugIds.includes(drug.id)) && !isAllSelected

  return (
    <Box>
      {/* 高度検索ボックス */}
      {enableAdvancedSearch ? (
        <Box sx={{ mb: 3 }}>
          <AdvancedSearchBox
            normalSearchValue={normalSearchTerm}
            onNormalSearchChange={onNormalSearchChange}
            onNormalSearch={onNormalSearch}
            onAdvancedSearch={onAdvancedSearch}
            placeholder={searchPlaceholder}
            isAdvancedMode={isAdvancedMode}
            onAdvancedModeChange={onAdvancedModeChange}
            initialAdvancedConditions={initialAdvancedConditions}
          />
        </Box>
      ) : null}

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 選択モードの場合の追加アクション */}
      {mode === 'selection' && data.length > 0 && !loading && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {selectedDrugIds.length} 件選択中
            </Typography>
            {onSelectAllResults && currentSearchKeyword && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAllResults}
              >
                検索結果をすべて選択
              </Button>
            )}
          </Stack>
        </Box>
      )}

      <TableContainer
        component={Paper}
        sx={{
          mb: 2,
          maxHeight: ADVANCED_SEARCH.TABLE_MAX_HEIGHT,
          overflow: 'auto',
        }}
      >
        <Table stickyHeader aria-label="医薬品テーブル">
          <TableHead>
            <TableRow>
              {/* 選択モードの場合はチェックボックス列を追加 */}
              {mode === 'selection' && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    indeterminate={isIndeterminate}
                    aria-label="すべて選択"
                  />
                </TableCell>
              )}

              <TableCell>YJコード</TableCell>
              <TableCell>商品名</TableCell>
              <TableCell>一般名</TableCell>
              <TableCell>製造元</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={mode === 'selection' ? 6 : 5}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2 }}>検索中...</Typography>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={mode === 'selection' ? 6 : 5}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography>
                    {currentSearchKeyword
                      ? '検索条件に一致する医薬品が見つかりませんでした'
                      : '医薬品データがありません'}
                  </Typography>
                  {currentSearchKeyword && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      検索条件を変更してお試しください
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              data.map((drug) => (
                <TableRow key={drug.id} hover>
                  {/* 選択モードの場合はチェックボックス列を追加 */}
                  {mode === 'selection' && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDrugIds.includes(drug.id)}
                        onChange={(e) =>
                          handleSelectionChange(drug.id, e.target.checked)
                        }
                        aria-label={`${
                          drug.product_name || drug.general_name
                        }を選択`}
                      />
                    </TableCell>
                  )}

                  <TableCell>
                    <Typography variant="body2" component="span">
                      {drug.yj_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {drug.product_name ? (
                      <Typography variant="body2">
                        {drug.product_name}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        【一般名のみ】
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {drug.general_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{drug.manufacturer}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {onViewDetails && (
                      <CustomButton
                        variant="outlined"
                        size="small"
                        onClick={() => onViewDetails(drug.id)}
                      >
                        詳細
                      </CustomButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページネーション（総ページ数が1より大きい場合のみ表示） */}
      {onPageChange && totalPages > 1 && (
        <PaginationControl
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </Box>
  )
}

export default EnhancedDrugTable
